import { useCallback, useRef } from 'react';
import {
  OwcModalDialog,
  OwcComponentHeader,
  OwcComponentFooter,
  OwcButton,
  OwcIcon,
} from '@one/react';
import { useHardDeleteFlow } from '../../../../hooks/useHardDeleteFlow';
import HardDeleteForm from './HardDeleteForm';
import HardDeleteReview from './HardDeleteReview';
import styles from './HardDeleteReleaseModal.module.css';

/**
 * Orchestrator for the hard-delete (latest release) flow. State and side effects
 * live in `useHardDeleteFlow`; the two steps render as `HardDeleteForm` and
 * `HardDeleteReview`. This component only wires them together.
 *
 * Hard delete permanently removes the latest release so the same release#version
 * PK can be recreated — used when the latest release was created by mistake.
 */
export default function HardDeleteReleaseModal({
  open,
  onClose,
  groupName,
  releaseVersion,
  userEmail,
  onDeleted,
}) {
  const modalRef = useRef(null);

  const {
    state,
    isLoading,
    setField,
    goToForm,
    runDryRun,
    execute,
    filteredEntities,
    emailValid,
    canRunDryRun,
    canExecute,
    actionCounts,
  } = useHardDeleteFlow({ groupName, releaseVersion, email: userEmail, open, onDeleted, onClose });

  const setActiveTab = useCallback((v) => setField('activeTab', v), [setField]);
  const setSearch = useCallback((v) => setField('search', v), [setField]);
  const setReason = useCallback((v) => setField('reason', v), [setField]);
  const setDeleteEntities = useCallback((v) => setField('deleteEntities', v), [setField]);
  const setConfirmVersion = useCallback((v) => setField('confirmVersion', v), [setField]);

  const handleClose = () => {
    if (isLoading) return;
    onClose?.();
  };

  const handleDryRun = (e) => {
    e?.preventDefault?.();
    // Guard against form submit (Enter key) bypassing the disabled button.
    if (!canRunDryRun || isLoading) return;
    runDryRun();
  };

  return (
    <OwcModalDialog
      visible={open}
      size="large"
      ref={modalRef}
      onVisibleChange={handleClose}
      backdropOpacity={0.7}
      style={{ zIndex: 6000 }}
    >
      <OwcComponentHeader slot="header" icon="delete" status="error" separator>
        Delete Release : {releaseVersion}
        <div slot="suffix" className={styles.headerSuffix}>
          <OwcButton
            onClick={handleClose}
            variant="tertiary"
            aria-label="Close"
            rounded="true"
            disabled={isLoading}
          >
            ✕
          </OwcButton>
        </div>
      </OwcComponentHeader>

      {state.step === 'form' && (
        <HardDeleteForm
          reason={state.reason}
          onReasonChange={setReason}
          deleteEntities={state.deleteEntities}
          onDeleteEntitiesChange={setDeleteEntities}
          emailValid={emailValid}
          errorMessage={state.errorMessage}
          isLoading={isLoading}
          onSubmit={handleDryRun}
        />
      )}

      {state.step === 'review' && (
        <HardDeleteReview
          plan={state.plan}
          releaseVersion={releaseVersion}
          deleteEntities={state.deleteEntities}
          actionCounts={actionCounts}
          filteredEntities={filteredEntities}
          activeTab={state.activeTab}
          search={state.search}
          confirmVersion={state.confirmVersion}
          errorMessage={state.errorMessage}
          isLoading={isLoading}
          onTabChange={setActiveTab}
          onSearchChange={setSearch}
          onConfirmChange={setConfirmVersion}
        />
      )}

      <OwcComponentFooter slot="footer" separator>
        <div className={styles.footerBar}>
          <div>
            {state.step === 'review' && (
              <OwcButton variant="tertiary" size="medium" onClick={goToForm} disabled={isLoading}>
                <OwcIcon name="chevron_left" family="outlined" /> Back
              </OwcButton>
            )}
          </div>
          <div className={styles.footerActions}>
            <OwcButton variant="secondary" size="medium" onClick={handleClose} disabled={isLoading}>
              Cancel
            </OwcButton>
            {state.step === 'form' ? (
              <OwcButton
                variant="primary"
                size="medium"
                onClick={handleDryRun}
                disabled={!canRunDryRun || isLoading}
              >
                Run dry-run analysis
              </OwcButton>
            ) : (
              <OwcButton
                variant="primary"
                size="medium"
                onClick={execute}
                disabled={!canExecute}
                style={{ '--one-button-background-color': 'var(--one-color-cobas-red-500, #c00)' }}
              >
                Delete release
              </OwcButton>
            )}
          </div>
        </div>
      </OwcComponentFooter>
    </OwcModalDialog>
  );
}
